/* The phase settings popover.
 *
 * No KANON specimen draws a phase editor, so nothing here is a transcription. Every value is borrowed
 * from a surface the canon DOES draw and that this app already ships: the popup wears the same card,
 * hairline, radius and neutral elevation as `shared/ui/MenuSurface`, the stepper is the tape's rest
 * stepper at the same 22px cell, the segmented pair is the rail's system control, and the two actions
 * borrow `ConfirmDialog`'s button geometry — the ghost verbatim, the destructive one drawn in ink
 * and edge rather than in that dialog's fill, for the reason recorded beside `.danger` below. That
 * is the whole design decision, and it is recorded here rather than presented as canon.
 *
 * `--text-3` is not used for text on this surface — see `PhaseBar.module.css` for the measurements
 * and the owner's standing decision behind that. */

/* Above the day dock, which is `z-index: 20`. It has to sit on the POSITIONER: Base UI positions
 * through inline styles there and the popup inside it is `position: static`, so `z-index` on the
 * popup applies to nothing at all. That exact mistake made the session card's menu visible,
 * hoverable and completely unclickable — see `MenuSurface.module.css`. */
.positioner {
  z-index: 40;
}

/* Bounded and scrolling past the bound — the same fix, and the same reasoning, as
 * `shared/ui/MenuSurface.module.css` records at length. This popover carries a name field, a phase
 * type pair, a cycle stepper and its acts, which is the tallest anchored popup in the builder and
 * therefore the first to run out of room on a short window. */
.popup {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 288px;
  max-height: var(--available-height, var(--overlay-max-block));
  padding: var(--space-4);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-pop);
}

.title {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-load);
  text-transform: uppercase;
  color: var(--text-2);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-0);
  margin: var(--space-0);
  border: 0;
}

.label {
  padding: var(--space-0);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.input {
  height: var(--h-control-field);
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
}

.input:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The rail's system control, at popover scale: two segments in one bordered group, the selected one
 * carrying the sunken fill and the heavier weight. Never colour alone — the weight and the fill are
 * two channels, and the radio's own `aria-checked` is the third. */
.segments {
  display: inline-flex;
  overflow: hidden;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
}

/* The frozen pair's second channel, and it is not decoration: the neutral chip the segments take
 * below separates live from refused clearly on the LIGHT canvas and almost not at all in Flame Dark,
 * where `--bg-chip` sits a hair off `--bg-card`. Measured on both themes before this was added.
 * The dashed edge is the same device — and the same reasoning — as the top bar's disabled undo and
 * redo buttons: a state carried by fill alone is a state one theme hides. */
.segments:has(.radio[aria-disabled='true']) {
  border-style: dashed;
}

.segment {
  display: grid;
  flex: 1;
  place-items: center;
  padding: var(--space-6px) var(--space-3);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  cursor: pointer;
}

.segment + .segment {
  border-left: var(--border-hairline) solid var(--border);
}

.segmentOn {
  font-weight: var(--fw-extrabold);
  color: var(--text);
  background: var(--bg-sunken);
}

.segment:hover {
  background: var(--hover-tint);
}

/* The radio itself is the accessible control and the label is its paint. Clipped rather than
 * `display: none`, which takes an input out of the accessibility tree and out of the tab order. */
.radio {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  clip-path: inset(50%);
}

.segment:has(.radio:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* Refused, focusable, and still carrying its reason — the same treatment `.step` and `.action` take
 * further down this file, and the same rule (view ADR §12): never the `disabled` attribute, which
 * drops the control out of the tab order and takes the explanation with it.
 *
 * `:has`, because `aria-disabled` sits on the RADIO — the radio is the accessible control and the
 * label is its paint, so a rule on the label alone could never see the state. Without this the pair
 * looked completely live on a phase the client has already trained into, and pressing either segment
 * did nothing at all; the reason was on screen under Delete and read as being about Delete.
 *
 * The neutral chip rather than an opacity, for `.action`'s reason: dimming multiplies the ink's
 * contrast down and takes the focus ring with it. It sits AFTER the focus ring above because it is
 * more specific than it — `no-descending-specificity`, and the ring has to keep winning anyway. */
.segment:has(.radio[aria-disabled='true']) {
  color: var(--text-2);
  cursor: not-allowed;
  background: var(--bg-chip);
}

.segment:has(.radio[aria-disabled='true']):hover {
  background: var(--bg-chip);
}

/* A frozen pair still has to say which type the phase IS, and the INK is what says it.
 *
 * Dimming both members to `--text-2` left the emphasis entirely to the fill, and in Flame Dark the
 * fill runs the wrong way: the refused chip (#1e1e21) is LIGHTER than the selected segment's sunken
 * surface (#0f0f10), so the option the phase is NOT became the loudest thing in the group. Measured
 * in the running app, both themes, before this was written.
 *
 * So the selected member keeps full `--text` and its heavier weight — the two channels that read the
 * same way on a light canvas and a dark one — and the fill goes back to being a surface rather than
 * the signal. What says REFUSED is the chip under the option that cannot be chosen, the dead cursor,
 * the dashed frame and the sentence below; none of those depend on which way round the greys go. */
.segmentOn:has(.radio[aria-disabled='true']) {
  color: var(--text);
  background: var(--bg-sunken);
}

/* The tape's rest stepper, laid out horizontally: same cell, same hairline group, same states. */
.stepper {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  overflow: hidden;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
}

.step {
  position: relative;
  display: grid;
  place-items: center;
  width: var(--h-stepper-cell);
  height: var(--h-stepper-cell);
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
}

.step::after {
  position: absolute;
  inset: var(--hit-extend);
  content: '';
}

.step:hover {
  color: var(--text);
  background: var(--bg-chip);
}

/* Dimmed, focusable and carrying its reason — never the `disabled` attribute (view ADR §12). The
 * weight and the dead cursor are what separate the two states; the colour step is not the channel. */
.step[aria-disabled='true'] {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  cursor: not-allowed;
}

.step[aria-disabled='true']:hover {
  color: var(--text-2);
  background: transparent;
}

/* Inset, because the group clips its own overflow — an outline outside the cell would be cut off
 * and leave a keyboard user with no ring. `--text` rather than the accent for the reason the rest
 * stepper records: on a cell fill the ember falls under the 3:1 SC 1.4.11 asks of an indicator. */
.step:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* A literal, like every other one-off width in these stylesheets: nothing else has to agree with it
 * to the pixel, which is the test `--w-plan-rail`'s own note gives for when a width earns a token.
 * Wide enough that "1 tydzień" and "12 tygodni" do not resize the group under the trainer's thumb. */
.value {
  min-width: 82px;
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-align: center;
  border-left: var(--border-hairline) solid var(--border);
  border-right: var(--border-hairline) solid var(--border);
  font-variant-numeric: tabular-nums;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: var(--border-hairline) solid var(--border);
}

.action {
  height: var(--h-control);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
  cursor: pointer;
  background: transparent;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  border-radius: var(--r-button);
}

/* Ink and edge, never a fill — and NOT `ConfirmDialog`'s treatment, which fills. The difference is
 * what the two controls are: that one is the answer, drawn as loudly as its consequence deserves,
 * and this one only OPENS the question. A filled red button in a settings popover would be the
 * loudest thing on a panel where nothing has been asked yet, and it would be the second one on
 * screen the moment the confirm it raises appears. */
.danger {
  color: var(--danger-text-strong);
  border-color: var(--danger-border);
}

.action:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.action:hover:not([aria-disabled='true']) {
  background: var(--hover-tint);
}

.danger:hover:not([aria-disabled='true']) {
  background: var(--danger-soft);
}

/* Neutral chip rather than an opacity: dimming multiplies the ink's contrast down and takes the
 * focus ring with it, so the one state that owes an explanation becomes the unreadable one. */
.action[aria-disabled='true'] {
  color: var(--text-2);
  cursor: not-allowed;
  background: var(--bg-chip);
  border-color: var(--border);
}

/* The reason a refused control cannot act, as ordinary on-screen text under it — a `title` reaches
 * neither a touch user nor a keyboard user who never hovers. */
.reason {
  font-size: var(--fs-tag);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
}
